import { useEffect, useRef } from 'react';

function BacktestChart({ data, trades, pair }) {
  const canvasRef = useRef(null);
  const containerRef = useRef(null);

  useEffect(() => {
    if (!data || data.length === 0 || !canvasRef.current || !containerRef.current) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const container = containerRef.current;
    
    // Set canvas size
    const width = container.offsetWidth;
    const height = 400;
    canvas.width = width;
    canvas.height = height;

    // Clear canvas
    ctx.clearRect(0, 0, width, height);

    // Calculate scales
    const padding = 40;
    const chartWidth = width - padding * 2;
    const chartHeight = height - padding * 2;
    
    const minPrice = Math.min(...data.map(d => d.y)) * 0.98;
    const maxPrice = Math.max(...data.map(d => d.y)) * 1.02;
    const priceRange = maxPrice - minPrice;

    const xScale = chartWidth / (data.length - 1);
    const yScale = chartHeight / priceRange;

    // Draw background gradient
    const bgGradient = ctx.createLinearGradient(0, 0, 0, height);
    bgGradient.addColorStop(0, 'rgba(0, 255, 127, 0.05)');
    bgGradient.addColorStop(1, 'rgba(0, 0, 0, 0.8)');
    ctx.fillStyle = bgGradient;
    ctx.fillRect(0, 0, width, height);

    // Draw grid lines
    ctx.strokeStyle = 'rgba(255, 255, 255, 0.05)';
    ctx.lineWidth = 1;
    for (let i = 0; i <= 5; i++) {
      const y = padding + (chartHeight / 5) * i;
      ctx.beginPath();
      ctx.moveTo(padding, y);
      ctx.lineTo(width - padding, y);
      ctx.stroke();
    }

    // Draw price line
    ctx.beginPath();
    ctx.strokeStyle = '#00FF7F';
    ctx.lineWidth = 2;
    
    data.forEach((point, index) => {
      const x = padding + index * xScale;
      const y = height - padding - (point.y - minPrice) * yScale;
      
      if (index === 0) {
        ctx.moveTo(x, y);
      } else {
        ctx.lineTo(x, y);
      }
    });
    ctx.stroke();

    // Draw area under line
    ctx.lineTo(width - padding, height - padding);
    ctx.lineTo(padding, height - padding);
    ctx.closePath();
    const areaGradient = ctx.createLinearGradient(0, padding, 0, height - padding);
    areaGradient.addColorStop(0, 'rgba(0, 255, 127, 0.2)');
    areaGradient.addColorStop(1, 'rgba(0, 255, 127, 0.01)');
    ctx.fillStyle = areaGradient;
    ctx.fill();

    // Draw trades
    trades.forEach((trade) => {
      // Validate trade data
      if (!trade.entryIndex || !trade.exitIndex || !trade.entryPrice || !trade.exitPrice) return;
      if (trade.entryIndex >= data.length || trade.exitIndex >= data.length) return;
      
      const entryX = padding + trade.entryIndex * xScale;
      const exitX = padding + trade.exitIndex * xScale;
      const entryY = height - padding - (trade.entryPrice - minPrice) * yScale;
      const exitY = height - padding - (trade.exitPrice - minPrice) * yScale;

      // Trade line
      ctx.beginPath();
      ctx.strokeStyle = trade.type === 'WIN' ? 'rgba(34, 197, 94, 0.6)' : 'rgba(239, 68, 68, 0.6)';
      ctx.lineWidth = 2;
      ctx.setLineDash([5, 5]);
      ctx.moveTo(entryX, entryY);
      ctx.lineTo(exitX, exitY);
      ctx.stroke();
      ctx.setLineDash([]);

      // Entry marker
      ctx.beginPath();
      ctx.arc(entryX, entryY, 6, 0, Math.PI * 2);
      ctx.fillStyle = trade.type === 'WIN' ? '#22C55E' : '#EF4444';
      ctx.fill();
      ctx.strokeStyle = '#fff';
      ctx.lineWidth = 2;
      ctx.stroke();

      // Exit marker
      ctx.beginPath();
      ctx.arc(exitX, exitY, 6, 0, Math.PI * 2);
      ctx.fillStyle = trade.type === 'WIN' ? '#22C55E' : '#EF4444';
      ctx.fill();
      ctx.strokeStyle = '#fff';
      ctx.lineWidth = 2;
      ctx.stroke();

      // Draw profit/loss label
      const labelY = Math.min(entryY, exitY) - 15;
      ctx.font = '11px monospace';
      ctx.fillStyle = trade.type === 'WIN' ? '#22C55E' : '#EF4444';
      const profitText = `${trade.type === 'WIN' ? '+' : ''}R${trade.profit}`;
      const textWidth = ctx.measureText(profitText).width;
      ctx.fillText(profitText, exitX - textWidth / 2, labelY);
    });

    // Draw price labels
    ctx.font = '12px sans-serif';
    ctx.fillStyle = 'rgba(255, 255, 255, 0.6)';
    for (let i = 0; i <= 5; i++) {
      const price = minPrice + (priceRange / 5) * i;
      const y = height - padding - (chartHeight / 5) * i;
      ctx.fillText(price.toFixed(0), 5, y + 4);
    }

    // Draw pair label
    ctx.font = 'bold 16px sans-serif';
    ctx.fillStyle = '#00FF7F';
    ctx.fillText(pair, padding, 25);

    // Draw time labels
    ctx.font = '11px sans-serif';
    ctx.fillStyle = 'rgba(255, 255, 255, 0.5)';
    const timePoints = [0, Math.floor(data.length / 4), Math.floor(data.length / 2), Math.floor(data.length * 3 / 4), data.length - 1];
    timePoints.forEach((index) => {
      const x = padding + index * xScale;
      ctx.fillText(`Day ${Math.floor(index / 4)}`, x - 15, height - 15);
    });

  }, [data, trades, pair]);

  return (
    <div ref={containerRef} className="w-full bg-zinc-950 rounded-xl border border-zinc-800 p-4">
      <canvas ref={canvasRef} className="w-full" />
      
      {/* Legend */}
      <div className="flex items-center justify-center gap-6 mt-4 text-xs">
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded-full bg-green-500"></div>
          <span className="text-gray-400">Winning Trade</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded-full bg-red-500"></div>
          <span className="text-gray-400">Losing Trade</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-0.5 bg-[#00FF7F]"></div>
          <span className="text-gray-400">Price Movement</span>
        </div>
      </div>
    </div>
  );
}

export default BacktestChart;
